import { Inject, Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom, tap } from 'rxjs';
import { API_BASE } from '../../ordering/data/ordering-api';
import { DevicePushService, PushSurface } from '../../../Shared/device-push.service';
import { ApiResponse, LoginResponse, PartnerRegisterRequest, PartnerUser } from './partner.models';

const TOKEN_KEY = 'orderyaa.partner.access';
const REFRESH_KEY = 'orderyaa.partner.refresh';
const USER_KEY = 'orderyaa.partner.user';

/** Web-push wiring for the partner (company-user) audience → /api/notifications/device/push-token. */
const PARTNER_PUSH: PushSurface = {
  key: 'partner',
  registerUrl: `${API_BASE}/api/notifications/device/push-token`,
  unregisterUrl: `${API_BASE}/api/notifications/device/push-token`,
};

/**
 * Partner (store owner) auth — reuses the COMPANY-USER auth on the backend:
 *   register → POST /api/Company (company + branch + BU + owner user); returns NO token
 *   login    → POST /api/Auth/login ({ username, password }); returns a company/branch/BU-scoped JWT
 * Session (token + refresh + user) is signal-based and persisted to localStorage (browser only);
 * partnerAuthInterceptor attaches the token to /api/sales, /api/Company and /api/hr calls.
 */
@Injectable({ providedIn: 'root' })
export class PartnerAuthService {
  private readonly http = inject(HttpClient);
  private readonly push = inject(DevicePushService);
  private readonly isBrowser: boolean;

  private readonly _token = signal<string | null>(null);
  private readonly _refresh = signal<string | null>(null);
  private readonly _user = signal<PartnerUser | null>(null);

  readonly isAuthenticated = computed(() => !!this._token());
  readonly user = this._user.asReadonly();
  /** True once the owner has verified their email (from the login user DTO / verify flow). */
  readonly emailVerified = computed(() => !!this._user()?.isEmailVerified);

  /** Shared in-flight refresh so concurrent 401s trigger ONE refresh call — otherwise each
   *  request rotates the refresh token out from under the others and they spuriously log out. */
  private _refreshPromise: Promise<boolean> | null = null;

  /** Bumped by clearSession(); a refresh captures it and discards its result if it changed, so a
   *  refresh in flight at logout (e.g. the logout push-token DELETE proactively refreshing an expired
   *  token) can't RESURRECT the session after teardown. Mirrors CustomerAuthService. */
  private _sessionEpoch = 0;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this._token.set(localStorage.getItem(TOKEN_KEY));
      this._refresh.set(localStorage.getItem(REFRESH_KEY));
      const raw = localStorage.getItem(USER_KEY);
      if (raw) {
        try { this._user.set(JSON.parse(raw)); } catch { /* ignore malformed cache */ }
      }
      // Returning signed-in owner: silently refresh the push token (no re-prompt) on app open.
      if (this._token()) void this.push.syncIfPermitted(PARTNER_PUSH);
    }
  }

  get accessToken(): string | null {
    return this._token();
  }

  get userId(): string | null {
    return (this._user()?.id as string | undefined) ?? null;
  }

  /** Flip the locally-stored user to verified after the backend verify-email call succeeds. */
  markEmailVerified(): void {
    const u = this._user();
    if (!u) return;
    const next = { ...u, isEmailVerified: true };
    this._user.set(next);
    if (this.isBrowser) localStorage.setItem(USER_KEY, JSON.stringify(next));
  }

  /** Create the company + branch + business unit + owner user. Returns NO token (see login()). */
  register(body: PartnerRegisterRequest): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${API_BASE}/api/Company`, body);
  }

  /** Log in with the owner's email (sent as `username`) + password → scoped JWT. */
  login(email: string, password: string): Observable<ApiResponse<LoginResponse>> {
    return this.http
      .post<ApiResponse<LoginResponse>>(`${API_BASE}/api/Auth/login`, { username: email, password })
      // Register push only on explicit sign-in (setSession is also called on silent refresh).
      .pipe(tap((res) => { if (res.success && res.data) { this.setSession(res.data); void this.push.register(PARTNER_PUSH); } }));
  }

  /** Forgot password — step 1. Email a one-time reset code to the owner's registered email
   *  (reuses the company-user OTP reset; always succeeds, doesn't reveal whether the email exists). */
  requestPasswordReset(email: string): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${API_BASE}/api/Auth/otp/forgot-password/request`, { email });
  }

  /** Forgot password — step 2. Verify the emailed reset code WITHOUT consuming it, before revealing
   *  the new-password step (mirrors the customer /ordering reset flow). */
  verifyResetCode(email: string, code: string): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${API_BASE}/api/Auth/otp/forgot-password/verify-code`, { email, code });
  }

  /** Forgot password — step 3. Re-verify the emailed reset code and set the new password. */
  resetPassword(email: string, code: string, newPassword: string): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${API_BASE}/api/Auth/otp/forgot-password/verify`, { email, code, newPassword });
  }

  /**
   * Silently exchange the refresh token for a fresh access token (~15-min access tokens).
   * Returns true on success, false when there is no refresh token or the server rejects it.
   * Concurrent callers share the same in-flight request (see {@link _refreshPromise}).
   */
  refreshAccessToken(): Promise<boolean> {
    if (this._refreshPromise) return this._refreshPromise;

    const refreshToken = this._refresh();
    if (!refreshToken) return Promise.resolve(false);

    const epoch = this._sessionEpoch; // if the session is torn down before we resolve, don't resurrect it
    this._refreshPromise = firstValueFrom(
      this.http.post<ApiResponse<LoginResponse>>(`${API_BASE}/api/Auth/refresh-token`, { refreshToken }),
    )
      .then((res) => {
        if (epoch !== this._sessionEpoch) return false; // logged out meanwhile — discard this refresh
        if (res?.success && res.data?.accessToken) {
          this.setSession(res.data);
          return true;
        }
        return false;
      })
      .catch(() => false)
      .finally(() => { this._refreshPromise = null; });

    return this._refreshPromise;
  }

  // ── Token expiry (mirrors the ERP core AuthService) ───────────────────────────

  private decodeToken(token: string): { exp?: number } {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Invalid JWT');
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  }

  /** Epoch-ms the access token expires at, or null if absent/undecodable/no exp claim. */
  private getTokenExpiryMs(): number | null {
    const token = this._token();
    if (!token) return null;
    try { const p = this.decodeToken(token); return p?.exp ? p.exp * 1000 : null; }
    catch { return null; }
  }

  /** True when there is no token, or the current access token's exp has passed. */
  isTokenExpired(): boolean {
    const token = this._token();
    if (!token) return true;
    const exp = this.getTokenExpiryMs();
    if (exp == null) return false;        // no exp claim → treat as non-expiring
    return Date.now() >= exp;
  }

  /** True when the access token expires within `thresholdSeconds` (or is already gone). */
  private isTokenExpiringSoon(thresholdSeconds = 60): boolean {
    const exp = this.getTokenExpiryMs();
    if (exp == null) return false;
    return Date.now() >= exp - thresholdSeconds * 1000;
  }

  /**
   * Guard helper: resolves true when a usable session is in place — either a live access token,
   * or an expired one that we can silently refresh. Resolves false for a dead/absent session, so
   * a stale token no longer renders as "signed in" (the reported "session doesn't log out" bug).
   */
  async ensureAuthenticated(): Promise<boolean> {
    if (!this._token()) return false;
    if (!this.isTokenExpired()) return true;
    return this.refreshAccessToken();
  }

  /** Proactively refresh when the token is about to expire; no-op when it is still fresh. */
  async ensureFreshToken(thresholdSeconds = 60): Promise<boolean> {
    if (!this._token()) return false;
    if (!this.isTokenExpiringSoon(thresholdSeconds)) return true;
    return this.refreshAccessToken();
  }

  /** Local teardown only (no server round-trip) — used when the session is already dead. */
  clearSession(): void {
    this._sessionEpoch++; // invalidate any in-flight refresh so it can't repopulate tokens after teardown
    this._token.set(null);
    this._refresh.set(null);
    this._user.set(null);
    if (this.isBrowser) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      localStorage.removeItem(USER_KEY);
      this.push.clearLocal(PARTNER_PUSH); // forget the cached push token so the next sign-in re-registers
    }
  }

  /** User-initiated sign-out: revoke the refresh token server-side (best-effort), then clear. */
  logout(): void {
    const refresh = this._refresh();
    // Clear the device row server-side FIRST — the interceptor still has our live JWT here.
    void this.push.unregister(PARTNER_PUSH);
    this.clearSession();
    if (this.isBrowser && refresh) {
      this.http.post(`${API_BASE}/api/Auth/logout`, { refreshToken: refresh }).subscribe({ error: () => {} });
    }
  }

  private setSession(d: LoginResponse): void {
    this._token.set(d.accessToken);
    this._refresh.set(d.refreshToken);
    this._user.set(d.user ?? null);
    if (this.isBrowser) {
      localStorage.setItem(TOKEN_KEY, d.accessToken);
      if (d.refreshToken) localStorage.setItem(REFRESH_KEY, d.refreshToken);
      if (d.user) localStorage.setItem(USER_KEY, JSON.stringify(d.user));
    }
  }
}
